import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { FocusCard, UserFocusItem, ReflectionType, FocusInteractionOptions } from '@shared/types';
import { logCardInteraction } from '@shared/services/cardEngine';
import { markFocusComplete } from '@shared/services/userContentService';
import { trackEvent } from '@shared/services/analyticsTracker';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { logError } from '@/lib/log';

interface UseFocusCompletionParams {
    focusCards: FocusCard[];
    setFocusCards: React.Dispatch<React.SetStateAction<FocusCard[]>>;
    focusItems: UserFocusItem[];
    setWellnessScore: React.Dispatch<React.SetStateAction<number>>;
    setMindfulMinutes: React.Dispatch<React.SetStateAction<number>>;
}

interface UseFocusCompletionReturn {
    markFocusCardComplete: (id: string, responses?: Record<string, string | number | string[]>, options?: FocusInteractionOptions) => void;
    connectBank: () => Promise<void>;
    isPaymentModalOpen: boolean;
    setIsPaymentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handlePaymentComplete: () => void;
}

export const useFocusCompletion = ({
    focusCards,
    setFocusCards,
    focusItems,
    setWellnessScore,
    setMindfulMinutes
}: UseFocusCompletionParams): UseFocusCompletionReturn => {
    const { currentUser } = useAuth();
    const { payerInfo } = useData();
    const navigate = useNavigate();

    // Payment Modal State
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [pendingPaymentCardId, setPendingPaymentCardId] = useState<string | null>(null);

    // Connect card state
    const [pendingConnectCardId, setPendingConnectCardId] = useState<string | null>(null);

    const finishFocusCompletion = async (
        id: string,
        responses?: Record<string, string | number | string[]>,
        options?: FocusInteractionOptions,
    ) => {
        // Track focus completion in GA4 + Fullstory + PostHog
        const completedCard = focusCards.find(h => h.id === id);
        const focusItem = focusItems.find(item => item.id === id);
        trackEvent('focus_completed', {
            card_type: completedCard?.type ?? 'unknown',
            category: completedCard?.category ?? 'unknown',
            content_type: focusItem?.contentType ?? 'focus',
            // Library identity (Content Engine v2, phase 0): libraryId is the
            // focusLibrary id; video/exercise items also carry contentRef —
            // their videoLibrary/exerciseLibrary id.
            library_id: focusItem?.libraryId ?? undefined,
            content_ref: focusItem?.contentRef ?? undefined,
            sequence_id: focusItem?.sequenceId ?? undefined,
            sequence_step: focusItem?.sequenceStep ?? undefined,
            track_id: focusItem?.trackId ?? undefined,
            delivery_source: focusItem?.deliverySource ?? 'legacy',
            // Time on step when the daily session measured it; absent otherwise.
            duration_ms: options?.durationMs ?? undefined,
        });

        // 1. Update UI instantly
        setFocusCards(prev => prev.map(h => h.id === id ? { ...h, completedToday: true, streak: h.streak + 1 } : h));
        setWellnessScore(prev => Math.min(prev + 5, 100)); // Gamify resilience

        // If card is related to mindfulness, boost minutes (mock)
        const card = focusCards.find(h => h.id === id);
        if (card?.category === 'mindset') {
            setMindfulMinutes(prev => prev + 5);
        }

        // 2. Log to Backend
        if (currentUser && focusItems.some(item => item.id === id)) {
            markFocusComplete(currentUser.uid, id, responses, options).catch(error => {
                logError(error, { context: 'mark_focus_complete' });
            });
        } else {
            logCardInteraction(id, 'complete');
        }
    };

    const markFocusCardComplete = (
        id: string,
        responses?: Record<string, string | number | string[]>,
        options?: FocusInteractionOptions,
    ) => {
        // Check if this is a financial interaction requiring a modal
        const card = focusCards.find(h => h.id === id);
        if (card?.type === 'financial-action') {
            // Safety net: only open payment modal if payer is verified with a linked account
            if (payerInfo.status === 'verified' && payerInfo.accountId) {
                setPendingPaymentCardId(id);
                setIsPaymentModalOpen(true);
                return;
            }
            // Not verified — skip payment modal and just complete the card
            finishFocusCompletion(id, responses, options);
            return;
        }

        if (card?.type === 'spinwheel-connect' || card?.type === 'plaid-connect') {
            setPendingConnectCardId(id);
            navigate('/connect');
            const provider = card.type === 'spinwheel-connect' ? 'spinwheel' : 'plaid';
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('navigateToConnect', { detail: { provider } }));
            }, 100);
            return;
        }

        finishFocusCompletion(id, responses, options);

        // Navigate to goals page after animation if user chose "Set Another Goal"
        if (responses?.goal_action === 'set_goal') {
            setTimeout(() => navigate('/goals'), 1000);
        }
    };

    const handlePaymentComplete = () => {
        if (pendingPaymentCardId) {
            finishFocusCompletion(pendingPaymentCardId);
            setPendingPaymentCardId(null);
        }
        setIsPaymentModalOpen(false);
    };

    const connectBank = async () => {
        return new Promise<void>(resolve => {
            setTimeout(() => {
                alert("In a real app, this would open Plaid Link!");
                setWellnessScore(prev => Math.min(prev + 10, 100));
                resolve();
            }, 2000);
        });
    };

    // Listen for connection success event (from ConnectView after spinwheel-connect / plaid-connect)
    useEffect(() => {
        const handleConnectionSuccess = () => {
            if (pendingConnectCardId) {
                finishFocusCompletion(pendingConnectCardId);
                setPendingConnectCardId(null);
            }
        };

        window.addEventListener('connectionSuccess', handleConnectionSuccess);
        return () => {
            window.removeEventListener('connectionSuccess', handleConnectionSuccess);
        };
    }, [pendingConnectCardId]);

    return {
        markFocusCardComplete,
        connectBank,
        isPaymentModalOpen,
        setIsPaymentModalOpen,
        handlePaymentComplete
    };
};
